// PHASE 3 · Server-authoritative wallet/spin path wired into the Phase 1 shell.
// The JSX below is byte-for-byte identical in structure/classNames/testids
// to the Phase 1 approved shell — visuals are frozen.  The wallet, RNG,
// and evaluator all live on the server now (see backend/routes/lucky_ducky_play.py).
//
// See engine/paylines.js, engine/paytable.js, engine/reelStrips.js,
// engine/reelEngine.js, engine/wager.js, engine/bonusState.js and
// spec/*.json for the Cardinal V2 source of truth (reel-strip animation
// frames still use the client copy for rolling motion only — actual
// stops come from the server response).
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SYMBOLS, ART } from './engine/symbols';
import { REEL_STRIPS } from './engine/reelStrips';
import { REEL_COUNT, PAYLINES_20 } from './engine/reelEngine';
import { WAGER_KEYS, WAGER_CREDITS, WAGER_LABELS, DENOM_CENTS, creditsToDollars } from './engine/wager';
import { RULES } from './engine/paytable';
import { createAudioManager } from './audio/AudioManager';
import { LUCKY_DUCKY_PREMIUM_PACK } from './audio/premiumPack';
import PaytableModal from './PaytableModal';
import { JackpotStrip } from '../../components/jackpot/JackpotStrip';
import { JackpotWinOverlay } from '../../components/jackpot/JackpotWinOverlay';
import { requestOnboardingSlot, releaseOnboardingSlot } from '../../onboardingQueue';
import { createGamePlatform } from '@platform';
import './LuckyDucky.css';

// ── Betohalics game platform (kiosk-ready architecture) ───────────────────
// The game touches the wallet ONLY through this platform's WalletPort and
// receives standardized input via its event bus. In `online` mode (default)
// the WebWalletAdapter issues the exact same /config and /spin requests the
// game used before — behavior is byte-identical. A future kiosk build swaps
// the adapters (bill acceptor, local credit meter) with no game changes.
const gamePlatform = createGamePlatform({
  gameId: 'lucky-ducky',
  gameVersion: 'phase3',
  gameSlug: 'lucky-ducky',
  denominations: [{ label: `${DENOM_CENTS}c`, cents: DENOM_CENTS }],
  betOptions: WAGER_KEYS.map((k) => ({ key: k, credits: WAGER_CREDITS[k] })),
});
const ldWallet = gamePlatform.wallet;
const ldBus = gamePlatform.bus;
const ldLog = gamePlatform.logger;
const LD_EVENTS = gamePlatform.events;

// Format credits as "$X.XX" for HUD display (1¢ denom → credits × 0.01).
const fmtDollars = (credits) => `$${creditsToDollars(credits).toFixed(2)}`;

// Convert server-authoritative dollar balance → credits (int) for the HUD.
const dollarsToCredits = (dollars) => Math.round((dollars * 100) / DENOM_CENTS);

// Generate a UUID for idempotency keys. Falls back to a random string for
// older browsers.
const newRequestId = () => (typeof crypto !== 'undefined' && crypto.randomUUID)
  ? crypto.randomUUID()
  : `req-${Date.now()}-${Math.random().toString(36).slice(2)}`;

// Module-scope audio manager so its state (mute preference, loops)
// survives fast re-renders without spawning duplicate instances.
const audioManager = createAudioManager({
  storageKey: 'ld-audio-muted',
  soundPack: LUCKY_DUCKY_PREMIUM_PACK,
});

const LuckyDucky = () => {
  const navigate = useNavigate();

  // PHASE 3 · Wallet balance comes from the server. `credit` mirrors the
  // authoritative dollar balance (converted to credits for HUD display).
  const [credit, setCredit] = useState(0);
  const [balanceReady, setBalanceReady] = useState(false);
  const [activeProfileId, setActiveProfileId] = useState(null);
  const [wagerKey, setWagerKey] = useState(() => {
    try {
      const saved = localStorage.getItem('ld-last-wager');
      if (saved && WAGER_KEYS.includes(saved)) return saved;
    } catch (_e) { /* ignore */ }
    return 'PLAY_1X';
  });
  const wagerCredits = WAGER_CREDITS[wagerKey];
  // Remember the player's last chosen bet so it's pre-selected next visit.
  useEffect(() => {
    try { localStorage.setItem('ld-last-wager', wagerKey); } catch (_e) { /* ignore */ }
  }, [wagerKey]);

  const [win, setWin] = useState(0);              // credits won on last spin
  const [totalFeatureWin, setTotalFeatureWin] = useState(0);
  // Big-win celebration overlay: { label, amount, display } or null.
  const [bigWin, setBigWin] = useState(null);
  const [winMultiplier, setWinMultiplier] = useState(1);
  const [message, setMessage] = useState('GOOD LUCK · TAP SPIN');
  // Kept for legacy compatibility with existing UI logic; the source of
  // truth for mute is the AudioManager (`muted` state defined below).
  // The actual `soundOn` derivation happens right after `muted` is declared.
  const [paytableOpen, setPaytableOpen] = useState(false);
  const [paytableTab, setPaytableTab] = useState('PAYS');
  // Feature-status panel state (derived from authoritative spin results).
  const [featureAwardedTotal, setFeatureAwardedTotal] = useState(15);
  const [panelRespin, setPanelRespin] = useState(null); // { bonus } during a respin
  const [respinNotice, setRespinNotice] = useState(0); // countdown after 2-BONUS default respin
  // Premium loading splash shown on entry while music + reel/asset warmup
  // completes, so the game is fully ready the moment the player is in.
  const [loading, setLoading] = useState(true);
  const loadingRef = useRef(true);   // stable mirror of `loading` for the gesture handler

  // Initial idle grid — varied non-winning mix.
  const emptyGrid = useMemo(
    () => [
      ['PLUM', 'B1R', 'ORANGE'],
      ['RED_7', 'WATERMEL', 'BELL'],
      ['BONUS', 'B2R', 'RED_7'],
      ['WILD', 'B3R', 'BONUS'],
      ['ORANGE', 'BELL', 'WATERMEL'],
    ],
    []
  );
  const [grid, setGrid] = useState(emptyGrid);
  const [spinning, setSpinning] = useState(false);
  // Tap-to-stop: `stoppable` gates the SPIN button flipping to STOP; the ref
  // holds a fn (set per spin) that force-finishes the live reel animations.
  const [stoppable, setStoppable] = useState(false);
  const stopSpinRef = useRef(null);
  const [jackpotWin, setJackpotWin] = useState(null); // { tier, amount } — shared progressive hit
  const [winningCells, setWinningCells] = useState(new Set());
  // Winning PAYLINE overlay — each entry maps a winning line to its hit cells
  // so we can draw a glowing box + connector over exactly the reels that paid.
  const [winningLines, setWinningLines] = useState([]);
  const [lineBoxes, setLineBoxes] = useState(null);
  const [rollup, setRollup] = useState(0);   // animated count-up of the last win
  const boardRef = useRef(null);

  const [featureActive, setFeatureActive] = useState(false);
  const [freePlaysLeft, setFreePlaysLeft] = useState(0);
  // +N free-plays delta shown briefly when a retrigger award lands during
  // BETO RESPINS.  `null` = no delta on screen.  Bumping the timestamp
  // (`ts`) forces the React CSS animation to restart even when two
  // consecutive retriggers award the same N.
  const [freePlaysDelta, setFreePlaysDelta] = useState(null);
  // ×3 multiplier reveal chip — pops on winning feature spins.
  const [multReveal, setMultReveal] = useState(null);
  const [showFeatureIntro, setShowFeatureIntro] = useState(false);
  // Post-feature celebration / recap overlay.  `featureRecap` carries the
  // headline total win plus the round breakdown (free plays played +
  // retriggers) so players get a satisfying summary of the round.
  const [showFeatureComplete, setShowFeatureComplete] = useState(false);
  const [featureRecap, setFeatureRecap] = useState({ win: 0, plays: 0, retriggers: 0 });

  const [autoRemaining, setAutoRemaining] = useState(0);
  const autoRef = useRef(0);
  autoRef.current = autoRemaining;
  // Autoplay configuration (count + stop conditions). Held in a ref too so the
  // autoplay chain reads the latest values without stale closures.
  const [autoConfig, setAutoConfig] = useState({
    count: 25,
    stopOnFeature: true,
    stopOnBigWin: true,
    stopOnLowBalance: false,
    lowBalanceDollars: 5,
  });
  const autoConfigRef = useRef(autoConfig);
  autoConfigRef.current = autoConfig;
  const [autoPanelOpen, setAutoPanelOpen] = useState(false);
  // Snapshot of the most recent spin outcome, used to evaluate autoplay stops.
  const lastSpinRef = useRef({ featureTriggered: false, winCredits: 0, wagerCredits: 0, balanceDollars: 0 });
  // Server-configured payout factor (RTP/house-edge scalar) — used so the
  // paytable can show dollar pays that match what players actually win.
  const [payoutFactor, setPayoutFactor] = useState(1);
  // First-time "How to Play" intro (shown once per device).
  const [showHowTo, setShowHowTo] = useState(false);
  // Near-miss "bonus heating up" teaser (2 bonus ducks, no trigger).
  const [showTeaser, setShowTeaser] = useState(false);
  const teaserTimerRef = useRef(null);

  // Resolves the pending doSpin() promise when the player taps "START FREE PLAYS".
  const featureContinueResolveRef = useRef(null);
  // Auto-dismiss timer for the Bonus Recap overlay — held in a ref so we can
  // clear it on manual dismiss and on unmount (avoids setState-after-unmount
  // and a stale timer closing a freshly-opened recap).
  const featureCompleteTimerRef = useRef(null);

  // Synchronous double-tap guard — protects against React state async lag
  // that could otherwise let 2 rapid taps both bypass the spinning guard.
  const spinInFlightRef = useRef(false);

  // Refs to the 5 reel viewports + their inner strips (imperative animation).
  const reelRefs = useRef([]);
  const stripRefs = useRef([]);

  // Preload all approved LD symbol images so the first spin never flashes.
  // Uses `img.decode()` on top of a plain preload so the browser has the
  // pixels ready in its decoded cache — subsequent DOM insertions of the
  // same src cannot introduce a decode-latency blank frame.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Include composite layers (bell base + BAR plaque) so the first
    // spin never flashes a half-loaded symbol.
    const urls = new Set();
    Object.values(SYMBOLS).forEach(s => {
      [s.asset, s.bell, s.bar].forEach(u => { if (u) urls.add(u); });
    });
    urls.forEach(src => {
      const img = new Image();
      img.src = src;
      // Fire-and-forget decode; failures are non-blocking.
      if (typeof img.decode === 'function') {
        img.decode().catch(() => {});
      }
    });
  }, []);

  // ENTRY WARMUP + LOADING SPLASH.  Because the player reached this screen
  // via a tap in the lobby, the page already has user activation — so we
  // can unlock the AudioContext and start the background tune immediately
  // (no extra tap needed).  Meanwhile we preload every audio file so the
  // first win/feature cue is instant.  The splash holds for a short beat so
  // music + reels are fully ready by the time it clears.
  useEffect(() => {
    // Unlock the audio pipeline within the entry activation window so the
    // tune can start cleanly once loading finishes.  Music is NOT started
    // here — it begins only when the loading splash clears.
    try { audioManager.unlock(); } catch (_e) { /* deferred to first gesture */ }
    try {
      Object.values(LUCKY_DUCKY_PREMIUM_PACK).forEach((def) => {
        if (def && def.src) { const a = new Audio(); a.preload = 'auto'; a.src = def.src; }
      });
    } catch (_e) { /* preload is best-effort */ }
    // Minimum 7s splash to build anticipation, then reveal the game AND
    // start the background music at the same moment the page opens.
    const done = setTimeout(() => {
      loadingRef.current = false;
      setLoading(false);
      try {
        if (!audioManager.isMuted()) {
          // Start it if a gesture hasn't already (may still need a tap on
          // Safari), and ramp up the volume in case it was primed silently
          // during the splash so the tune swells in as the game reveals.
          audioManager.loop('background_music', { volume: 0.18 });
          audioManager.setLoopVolume('background_music', 0.18, 800);
        }
      } catch (_e) { /* fallback: first in-game gesture starts it */ }
    }, 7000);
    return () => clearTimeout(done);
  }, []);

  // Full-screen BIG WIN celebration: coin-burst + animated count-up.  Fires
  // when a single spin pays >= 20x the bet (MEGA >= 50x, EPIC >= 100x).
  // Returns a promise so the spin flow holds while the moment plays out.
  const celebrateBigWin = useCallback(async (winCredits, wagerCr) => {
    const ratio = winCredits / Math.max(1, wagerCr);
    if (ratio < 20) return false;
    const label   = ratio >= 100 ? 'EPIC WIN' : ratio >= 50 ? 'MEGA WIN' : 'BIG WIN';
    const countMs = ratio >= 100 ? 3200 : ratio >= 50 ? 2600 : 2000;
    const holdMs  = ratio >= 100 ? 1600 : 1100;
    setBigWin({ label, amount: winCredits, display: 0 });
    const start = performance.now();
    await new Promise((res) => {
      const tick = (now) => {
        const t = Math.min(1, (now - start) / countMs);
        const eased = 1 - Math.pow(1 - t, 3);
        setBigWin((b) => (b ? { ...b, display: Math.round(winCredits * eased) } : b));
        if (t < 1) requestAnimationFrame(tick); else res();
      };
      requestAnimationFrame(tick);
    });
    await new Promise((r) => setTimeout(r, holdMs));
    setBigWin(null);
    return true;
  }, []);

  // Small pseudo-random per-spin jitter for visual stagger variety
  // (VISUAL ONLY — never affects server outcome or wins).
  const jitter = () => (Math.random() * 40) - 20;   // ±20ms

  // Preview-only hooks so QA can screenshot key overlays without waiting
  // for natural triggers. Server owns actual spin logic — these params
  // only manipulate the display state.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const p = new URLSearchParams(window.location.search);
    if (p.get('dev_feature') === '1') {
      setFreePlaysLeft(RULES.free_plays_awarded_on_trigger);
      setShowFeatureIntro(true);
    }
    if (p.get('dev_respin') === '1') {
      setRespinNotice(1);
      setMessage('BONUS · RESPIN FREE SPIN');
    }
  }, []);

  const play = (kind) => audioManager.play(kind);
  const [muted, setMutedState] = useState(audioManager.isMuted());
  const soundOn = !muted;
  const toggleMute = () => {
    const next = !muted;
    audioManager.setMuted(next);
    setMutedState(next);
    if (!next) {
      // Un-muting: resume the background tune (loop de-dupes) and give a
      // soft confirmation tick.
      audioManager.unlock();
      audioManager.loop('background_music', { volume: 0.18 });
      audioManager.play('ui_button');
    }
  };
  // Mobile audio unlock — first user gesture anywhere in the game unlocks
  // the AudioContext. iOS/Safari require this.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const unlock = () => {
      audioManager.unlock();
      // Kick off the low, non-fatiguing background tune on a real user
      // gesture (Safari/iOS block autoplay before this).  The loop() call
      // de-dupes, so repeated gestures never stack music.  We are NOT
      // `once`-only on purpose: on Safari the very first blocked attempt
      // could otherwise consume the listener and leave the game silent for
      // the whole session.  While the loading splash is still up we prime
      // the track at volume 0 (a valid in-gesture play() that Safari
      // allows) and the splash-clear handler ramps it up — this guarantees
      // music is actually playing by the time the game opens.
      if (!audioManager.isMuted()) {
        audioManager.loop('background_music', { volume: loadingRef.current ? 0 : 0.18 });
      }
    };
    const opts = { passive: true };
    window.addEventListener('pointerdown', unlock, opts);
    window.addEventListener('touchstart',  unlock, opts);
    window.addEventListener('keydown',     unlock, opts);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('touchstart',  unlock);
      window.removeEventListener('keydown',     unlock);
    };
  }, []);

  // Silence ALL game audio when leaving the game (component unmount) so the
  // background / BETO feature music never bleeds into the lobby or elsewhere.
  useEffect(() => {
    return () => {
      try {
        if (featureCompleteTimerRef.current) clearTimeout(featureCompleteTimerRef.current);
        audioManager.stopAllLoops();
        audioManager.stopAnticipation(null, true);
      } catch (_e) { /* ignore */ }
    };
  }, []);

  // PHASE 3 · Load server-authoritative wallet balance + active profile identity on mount.
  useEffect(() => {
    let cancelled = false;
    ldLog.event('game_launch', { mode: gamePlatform.mode });
    ldBus.emit(LD_EVENTS.GAME_LAUNCH, { gameId: 'lucky-ducky' });
    (async () => {
      try {
        const data = await ldWallet.getConfig();
        if (cancelled) return;
        setCredit(dollarsToCredits(data.balance_dollars));
        setActiveProfileId(data.active_profile?.profile_id || null);
        if (typeof data.payout_factor === 'number') setPayoutFactor(data.payout_factor);
        setBalanceReady(true);
        ldBus.emit(LD_EVENTS.GAME_READY, { balanceDollars: data.balance_dollars });
      } catch (e) {
        if (cancelled) return;
        ldLog.error('config_load_failed', { status: e?.response?.status });
        setMessage(e?.response?.status === 401
          ? 'PLEASE SIGN IN TO PLAY'
          : 'CONNECTING TO GAME SERVER…');
        setBalanceReady(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Show the one-time "How to Play" intro on first ever visit (per device).
  useEffect(() => {
    if (!balanceReady) return;
    try {
      if (!localStorage.getItem('ld-howto-seen')) {
        // Queue behind global onboarding modals so nothing stacks.
        requestOnboardingSlot('ld-howto', 5, () => setShowHowTo(true));
      }
    } catch (_e) { /* ignore */ }
  }, [balanceReady]);

  const dismissHowTo = () => {
    try { localStorage.setItem('ld-howto-seen', '1'); } catch (_e) { /* ignore */ }
    play('ui_button');
    setShowHowTo(false);
    releaseOnboardingSlot('ld-howto');
  };

  // Release the queue slot if the game unmounts before the how-to shows.
  useEffect(() => () => releaseOnboardingSlot('ld-howto'), []);

  const cycleWager = (delta) => {
    if (spinning || featureActive) return;
    play('ui_button');
    const i = WAGER_KEYS.indexOf(wagerKey);
    const next = Math.max(0, Math.min(WAGER_KEYS.length - 1, i + delta));
    setWagerKey(WAGER_KEYS[next]);
  };
  const maxBet = () => {
    if (spinning || featureActive) return;
    play('ui_button');
    // MAX = the highest bet the player can actually afford (falls back to the
    // top of the ladder when they can afford everything).
    const affordable = highestAffordableKey(credit);
    setWagerKey(affordable || 'MAX_PLAY');
  };

  // Highest wager key whose credit cost the player can currently cover.
  const highestAffordableKey = (creditBal) => {
    for (let i = WAGER_KEYS.length - 1; i >= 0; i--) {
      if (WAGER_CREDITS[WAGER_KEYS[i]] <= creditBal) return WAGER_KEYS[i];
    }
    return null;
  };
  const minBetCredits = WAGER_CREDITS[WAGER_KEYS[0]];
  const outOfCredits = balanceReady && credit < minBetCredits;

  // Auto-lower the selected bet when the balance can no longer cover it, so
  // players never sit on an unspinnable bet. Only clamps DOWN, never up, and
  // only while idle.
  useEffect(() => {
    if (!balanceReady || spinning || featureActive) return;
    if (credit >= wagerCredits) return;
    const affordable = highestAffordableKey(credit);
    if (affordable && affordable !== wagerKey) {
      setWagerKey(affordable);
      setMessage(`BET LOWERED TO ${fmtDollars(WAGER_CREDITS[affordable])}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credit, balanceReady, spinning, featureActive, wagerCredits, wagerKey]);

  // Highlight winning cells for one server-returned evaluation.
  // Distinct glow colors so multiple simultaneous winning lines stay readable.
  const LINE_COLORS = ['#ffd23a', '#4bd0ff', '#ff6ad5', '#7CFF6B', '#ff9f43', '#b388ff', '#ff5f5f', '#26e0c8'];
  const highlightWins = (evaluation) => {
    const cells = new Set();
    const lines = [];
    (evaluation.line_wins || []).forEach((w, i) => {
      const line = PAYLINES_20.find(l => l.id === w.line_id);
      if (!line) return;
      // `hits` = how many reels from the left formed the win (server-authored).
      const hits = (w.hits != null) ? w.hits : REEL_COUNT;
      for (let r = 0; r < hits; r++) cells.add(`${r}-${line.rows[r]}`);
      lines.push({ line_id: w.line_id, rows: line.rows, hits, color: LINE_COLORS[i % LINE_COLORS.length] });
    });
    setWinningCells(cells);
    setWinningLines(lines);
  };

  // Measure the real on-screen position of each winning reel column (after
  // the reels land) and build pixel-space boxes + connectors for the payline
  // overlay.  NOTE: the reel STRIP is imperatively DOM-mutated during the
  // spin animation, so the individual cells at rest are NOT React-managed and
  // carry no data-testid/`.win` class.  The `.ld-reel` COLUMN wrappers
  // (reelRefs) ARE stable React nodes, and each reel is an overflow-hidden
  // 1×3 window — so we derive each cell box geometrically as (reel box ÷ 3).
  useEffect(() => {
    if (spinning || winningLines.length === 0) { setLineBoxes(null); return; }
    const raf = requestAnimationFrame(() => {
      const board = boardRef.current;
      if (!board) return;
      const br = board.getBoundingClientRect();
      const cs = window.getComputedStyle(board);
      const bl = parseFloat(cs.borderLeftWidth) || 0;
      const bt = parseFloat(cs.borderTopWidth) || 0;
      // Origin = padding box (where an inset:0 absolute child sits).
      const originX = br.left + bl, originY = br.top + bt;
      const W = board.clientWidth, H = board.clientHeight;
      const out = [];
      const boxes = [];
      const seen = new Set();
      for (const L of winningLines) {
        const pts = [];
        for (let r = 0; r < L.hits; r++) {
          const reel = reelRefs.current[r];
          if (!reel) continue;
          const rr = reel.getBoundingClientRect();
          const rowH = rr.height / 3;
          const row = L.rows[r];
          const x = rr.left - originX;
          const y = (rr.top - originY) + row * rowH;
          const key = `${r}-${row}`;
          if (!seen.has(key)) { seen.add(key); boxes.push({ x, y, w: rr.width, h: rowH }); }
          pts.push(`${(x + rr.width / 2).toFixed(1)},${(y + rowH / 2).toFixed(1)}`);
        }
        if (pts.length > 1) out.push({ color: L.color, points: pts.join(' ') });
      }
      setLineBoxes({ w: W, h: H, boxes, lines: out });
    });
    return () => cancelAnimationFrame(raf);
  }, [winningLines, spinning]);

  // Credit roll-up — as the winning boxes glow, tick the paid amount up from
  // $0 to the spin's win so the payout feels earned.
  useEffect(() => {
    if (spinning || win <= 0) { setRollup(0); return; }
    let raf;
    const start = performance.now();
    const dur = Math.min(1100, 380 + win * 6); // bigger wins roll a touch longer
    const tick = (now) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setRollup(Math.max(0, win * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
      else setRollup(Math.max(0, win));
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [win, spinning]);

  // Animate a single grid landing on the reels — PREMIUM MOTION.
  // Each reel uses ONE Web Animations API animation with 4 keyframes and
  // per-segment easing, so the browser interpolates a single continuous
  // transform timeline instead of stitching 4 separate CSS transitions.
  // This eliminates the velocity discontinuities that made previous
  // reel motion read as "stepped" between phases.
  //
  //   0%              startPx  (offset above viewport, motion blur ON)
  //   ~7%   (150ms)   fast acceleration ends           · ease-in
  //   ~82%  (cruise)  linear high-speed reel travel    · linear
  //   ~97%  (decel)   controlled ease-out to overshoot · ease-out
  //   100%  (settle)  exact target                     · settle
  //
  // Reel-stop target times (left → right):
  //   R1 1450ms · R2 1620ms · R3 1790ms · R4 1960ms · R5 2130ms
  // (~170ms stagger per user spec).
  //
  // Server owns the final grid; animation ONLY reveals the authoritative
  // result — filler symbols are visual-only and never reach the viewport
  // window at rest.
  const animateGrid = (finalGrid, opts = {}) => new Promise((resolve) => {
    if (!stripRefs.current[0]) { setGrid(finalGrid); resolve(); return; }
    setSpinning(true);
    // B3 respin: reels listed in opts.lockReels stay frozen (held ducks) and
    // do NOT spin — only the remaining reels animate.
    const lockReels = Array.isArray(opts.lockReels) ? opts.lockReels : [];
    reelRefs.current.forEach((el) => { if (el) el.classList.remove('reel-locked'); });
    // Signal ambient background layers to throttle themselves during
    // active reel motion (CSS reads .ld-page.is-spinning to slow
    // caustics + bubbles so the reels never share GPU with them).
    if (typeof document !== 'undefined') {
      document.querySelector('.ld-page')?.classList.add('is-spinning');
    }
    // NORMAL reel-spin audio — BASE GAME ONLY (feature spins and the
    // 2-duck respin/anticipation keep their own separately-mapped audio).
    // Sequence: spin_start once → ONE subtle reel_spin_loop under the
    // motion.  The five reel-stop cues and the loop fade-out are driven by
    // the REAL visual land events below (anim.onfinish) — never a guess
    // timer — so the audio is physically attached to the reels.
    const baseSpinAudio = !featureActive && !opts.respinSlow;
    if (baseSpinAudio) {
      audioManager.play('spin_start');
      // reel_spin_loop (the whoosh WHILE the reels move) removed 2026-06 (operator) — annoying.
    }

    const reduceMotion = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    // Filler count — high enough that recycling remains offscreen
    // throughout the cruise phase even at ~2s durations.
    const N_FILLERS = reduceMotion ? 3 : 24;

    // Per-reel total spin duration (user spec, ~170ms stagger).
    // `speedMultiplier` scales each reel's total duration equally so
    // BETO RESPINS spins run ~15% faster (0.85×) while preserving the
    // 170ms inter-reel stagger, the accel/cruise/decel phases, and the
    // zero-flash landing.  Base game keeps the multiplier at 1.0.
    const SPEED = Math.max(0.5, Math.min(1.5,
      typeof opts.speedMultiplier === 'number' ? opts.speedMultiplier : 1.0));
    const BASE_MS = reduceMotion
      ? [180, 220, 260, 300, 340]
      : [1850, 2020, 2190, 2360, 2530];
    const REEL_STOP_MS = BASE_MS.map(ms => Math.round(ms * SPEED));

    // Prepare a shuffled filler pool per reel from ALL approved symbols
    // so adjacent reels never share identical filler cadences.
    const APPROVED_SYMBOLS = Object.keys(SYMBOLS);
    const shuffledFor = (reelIdx) => {
      const pool = [...APPROVED_SYMBOLS];
      for (let i = pool.length - 1; i > 0; i--) {
        const j = ((Math.random() * (i + 1)) | 0);
        [pool[i], pool[j]] = [pool[j], pool[i]];
      }
      const out = [];
      let last = null;
      for (let i = 0; i < N_FILLERS; i++) {
        let pick = pool[(i + reelIdx * 7) % pool.length];
        if (pick === last) pick = pool[(i + reelIdx * 7 + 3) % pool.length];
        out.push(pick);
        last = pick;
      }
      return out;
    };

    // Track per-reel side-effect timers so we can cancel if needed.
    const pendingTimers = [];
    const reelPromises = [];
    // Track the live WAAPI animations so that if iOS PAUSES them (e.g. Safari
    // is backgrounded to take a phone call), we can force-finish them the
    // moment the page returns — otherwise the reels can stay frozen and the
    // SPIN button never re-enables.
    const reelAnims = [];
    const onVisibleFinish = () => {
      if (document.visibilityState !== 'visible') return;
      reelAnims.forEach(a => {
        try { if (a && (a.playState === 'running' || a.playState === 'paused')) a.finish(); } catch (_e) { /* ignore */ }
      });
    };
    document.addEventListener('visibilitychange', onVisibleFinish);

    // ── BONUS ANTICIPATION · SUPER-SLOW FULL-PASS CRAWL ─────────────────
    // The AUTHORITATIVE server result tells us which reels contain a
    // BONUS duck.  The animation READS this; it never modifies it.
    //
    // Anticipation only exists once a bonus is genuinely IN PLAY *and*
    // still POSSIBLE.  A BETO trigger needs 3 bonus ducks, so if the first
    // duck hasn't appeared by the 3rd reel there is no way to reach 3 with
    // the reels that remain — a bonus is impossible, so every reel runs at
    // regular speed and the spin never drags.  Otherwise: reels run full
    // speed until the FIRST duck lands, then ONLY the reels to its right
    // drop into the slow crawl.
    //
    // During the crawl each remaining reel scrolls a couple of FULL passes
    // of symbols (CRAWL_CELLS) nice and slow before it settles on the
    // authoritative landing.  That long, readable creep builds the "is it
    // coming?!" suspense.
    //
    // Disabled during BETO RESPINS and reduced-motion.
    const bonusReels     = finalGrid.map(col => Array.isArray(col) && col.includes('BONUS'));
    const firstBonusReel = bonusReels.findIndex(Boolean);   // -1 = none
    // Feature needs 3 ducks; with ≤1 duck per reel a first duck landing on
    // reel index ≤ 2 still leaves enough reels to reach 3.  A first duck at
    // index 3+ (i.e. no duck through the 3rd reel) can never reach 3 →
    // no anticipation.
    const anticipationOn  = false;  // DISABLED 2026-06 (operator): 2 ducks do nothing special — no crawl, no whine, no spotlight. Removes the choppy slow-crawl entirely.

    // Cumulative BONUS count as reels resolve left→right; `secondDuckReel`
    // is the reel at which the SECOND duck is on-screen (handles two ducks
    // stacked on one reel).  The dramatic slow crawl + reel spotlight begin
    // only AFTER two ducks have fallen — never on just the first.  (The
    // rising whine audio can still build from the first duck.)
    let _cumDucks = 0;
    let secondDuckReel = Infinity;
    for (let i = 0; i < REEL_COUNT; i++) {
      _cumDucks += (finalGrid[i] || []).filter(s => s === 'BONUS').length;
      if (_cumDucks >= 2) { secondDuckReel = i; break; }
    }
    // B3 respin: force the readable slow-crawl on EVERY spinning (non-locked)
    // reel so the single 2-BONUS respin always slows down for suspense.
    const respinSlow = false;  // 2-duck respin removed (operator) — no slow respin crawl
    const slowOn = respinSlow || (anticipationOn && secondDuckReel !== Infinity
      && secondDuckReel <= REEL_COUNT - 2);
    // Reels AFTER slowRef crawl. respinSlow => -1 so all non-locked reels crawl;
    // otherwise the reel where the 2nd duck landed.
    const slowRef = respinSlow ? -1 : secondDuckReel;
    // The reels to the right enter the SLOW crawl exactly when the SECOND
    // bonus duck visibly lands (its scheduled stop time).
    const engageTime = !slowOn ? 0 : (respinSlow ? 300 : REEL_STOP_MS[secondDuckReel]);
    // Symbols each crawling reel scrolls slowly before landing — roughly
    // two full passes through the 3 visible rows (one extra pass vs before
    // for stronger anticipation).
    const CRAWL_CELLS   = respinSlow ? 2 : 4;
    // Slow crawl velocity + how much EXTRA each reel crawls versus the one
    // to its left.  Keeping a constant per-symbol speed but giving every
    // reel more distance than the previous one makes them clearly stop
    // one-at-a-time, left → right (no bunched "all stop together" finish).
    const MS_PER_CELL          = respinSlow ? 150 : (reduceMotion ? 45 : 620);   // per-symbol dwell (tightened from 1200 — suspense was too long)
    const EXTRA_CELLS_PER_REEL = respinSlow ? 1  : (reduceMotion ? 1  : 2);      // each reel to the right spins one extra pass more

    // ── DECIDING-REEL SLOWDOWN ─────────────────────────────────────────
    // Once TWO bonus ducks are already showing, the reels still crawling
    // could deliver the deciding 3rd duck (the BETO RESPINS trigger).
    // Those "deciding" reels (all crawl reels, since the crawl only starts
    // after the 2nd duck) creep slower to milk the suspense.
    const DECIDING_SLOWDOWN    = respinSlow ? 1.15 : (reduceMotion ? 1 : 1.3);   // deciding reel creeps this much slower (was 1.7)
    const DECIDING_EXTRA_CELLS = respinSlow ? 0    : (reduceMotion ? 0 : 1);     // + a little extra distance to drag it out (was 2)

    const reelLanded = new Array(REEL_COUNT).fill(false);
    // Running count of BONUS ducks visible as reels land left→right — drives
    // the staged anticipation audio (1st/2nd duck → rising whine; on the 2nd
    // duck the "you're one away!" win cue rings and the whine steps up).
    let bonusSeen = 0;
    // Spotlight state — once 2 ducks show we brighten the still-crawling
    // deciding reel(s) and dim the finished ones ("all eyes on that reel").
    let spotlightActive = false;
    const clearReelSpotlight = () => {
      reelRefs.current.forEach((el) => {
        if (el) el.classList.remove('deciding-spotlight', 'reel-dimmed');
      });
    };

    for (let r = 0; r < REEL_COUNT; r++) {
      const stripEl = stripRefs.current[r];
      const reelEl  = reelRefs.current[r];
      if (!stripEl || !reelEl) continue;

      const currentTop3 = grid[r];
      const finalCol    = finalGrid[r];

      // B3: locked (held) duck reels stay frozen — render statically, no spin.
      if (lockReels.includes(r)) {
        stripEl.style.transition = 'none';
        stripEl.style.transform  = 'translate3d(0,0,0)';
        stripEl.classList.remove('blur-hi', 'blur-lo');
        stripEl.innerHTML = finalCol.map(sid => {
          const s = SYMBOLS[sid];
          const inner = s.composite
            ? `<div class="ld-composite"><img class="ld-comp-bell" src="${s.bell}" alt="" draggable="false" decoding="sync"/><img class="ld-comp-bar" src="${s.bar}" alt="${s.label}" draggable="false" decoding="sync"/></div>`
            : `<img src="${s.asset}" alt="${s.label}" draggable="false" decoding="sync"/>`;
          return `<div class="ld-reel-cell${sid === 'BONUS' ? ' is-bonus is-locked' : ''}${sid === 'WILD' ? ' is-wild' : ''}" data-sym="${sid}">${inner}</div>`;
        }).join('');
        reelEl.classList.add('reel-locked');
        continue;
      }

      // Strip DOM layout (top → bottom):
      //   [final 3]  ← what lands in the viewport window
      //   [fillers]
      //   [current 3] ← starts in viewport window at t=0
      const seq = [...finalCol, ...shuffledFor(r), ...currentTop3];
      stripEl.innerHTML = seq.map(sid => {
        const s = SYMBOLS[sid];
        const inner = s.composite
          ? `<div class="ld-composite"><img class="ld-comp-bell" src="${s.bell}" alt="" draggable="false" decoding="sync"/><img class="ld-comp-bar" src="${s.bar}" alt="${s.label}" draggable="false" decoding="sync"/></div>`
          : `<img src="${s.asset}" alt="${s.label}" draggable="false" decoding="sync"/>`;
        return `<div class="ld-reel-cell${sid === 'BONUS' ? ' is-bonus' : ''}${sid === 'WILD' ? ' is-wild' : ''}" data-sym="${sid}">${inner}</div>`;
      }).join('');

      const cellPx   = reelEl.clientHeight / 3;
      const startPx  = -(seq.length - 3) * cellPx;
      const targetPx = 0;
      const scroll   = targetPx - startPx;   // positive

      // Is THIS reel a crawling anticipation reel? (Any reel to the right
      // of the first landed bonus.)
      const isCrawlReel = slowOn && r > slowRef;
      const accelMs = reduceMotion ? 40 : 150;

      // Prime start position + hi motion blur WITHOUT a stray CSS
      // transition (WAAPI owns the timeline below).
      stripEl.style.transition = 'none';
      stripEl.style.transform  = `translate3d(0, ${startPx}px, 0)`;
      stripEl.classList.remove('blur-lo');
      stripEl.classList.add('blur-hi');
      // Strip ALL per-image filters (drop-shadow) for the ENTIRE time this
      // reel is in motion — including the slow anticipation crawl (blur is
      // turned off during the crawl, but the drop-shadow was still forcing a
      // per-frame raster on mobile = the choppy crawl). Shadow returns on land.
      stripEl.classList.add('ld-spinning');
      void stripEl.offsetHeight;

      let anim, totalMs;

      if (isCrawlReel) {
        // TWO-PHASE: full-speed cruise up to a "hold" point reached exactly
        // as the first bonus lands, then a slow readable crawl that eases to
        // a DEAD STOP.  Each reel to the right crawls EXTRA_CELLS_PER_REEL
        // more symbols than the one before it (at the same slow velocity) so
        // the reels visibly stop one-at-a-time, left → right.
        const crawlOffset = r - slowRef - 1;              // 0 for the first crawl reel
        // Every crawl reel is a "deciding" reel (the crawl only begins after
        // the 2nd duck), so they all get the slower creep + extra distance.
        const isDecidingReel = true;
        const msPerCell   = isDecidingReel ? MS_PER_CELL * DECIDING_SLOWDOWN : MS_PER_CELL;
        const crawlCells  = CRAWL_CELLS + crawlOffset * EXTRA_CELLS_PER_REEL
                            + (isDecidingReel ? DECIDING_EXTRA_CELLS : 0);
        const crawlMs     = crawlCells * msPerCell;
        totalMs = engageTime + crawlMs;
        const holdPx     = targetPx - crawlCells * cellPx;   // crawlCells above the landing
        const fastDist   = holdPx - startPx;                 // positive
        const posAccelEnd = startPx + fastDist * 0.06;

        // Crawl motion profile — the key to a SMOOTH stop:
        //   • a long, constant-velocity readable creep, then
        //   • a constant-deceleration glide that BEGINS at exactly the
        //     creep velocity and eases to ZERO velocity at the landing.
        // Because the decel starts at the creep speed (no speed-up) and
        // ends at a true standstill, the reel never "kicks"/jumps at the
        // end — it just glides to a complete stop.  We sample the profile
        // into many linear keyframes so the browser reproduces it exactly.
        const crawlDist = targetPx - holdPx;                     // positive (= crawlCells*cellPx)
        const decelDist = Math.min(crawlDist * 0.5, 1.4 * cellPx); // final ~1.4 cells glide to rest
        const linearDist = crawlDist - decelDist;
        // crawlMs = linearDist/v + (2*decelDist)/v  →  v = (linearDist + 2*decelDist)/crawlMs
        const v    = (linearDist + 2 * decelDist) / crawlMs;     // px per ms (creep velocity)
        const tLin = linearDist / v;                             // constant-velocity time
        const tDec = crawlMs - tLin;                             // = 2*decelDist/v

        const kf = [
          { offset: 0,                      transform: `translate3d(0, ${startPx}px, 0)`,      easing: 'cubic-bezier(0.42, 0, 0.85, 0.35)' },  // accel
          { offset: accelMs / totalMs,      transform: `translate3d(0, ${posAccelEnd}px, 0)`,  easing: 'linear' },                              // fast cruise
          { offset: engageTime / totalMs,   transform: `translate3d(0, ${holdPx}px, 0)`,       easing: 'linear' },                              // enter SLOW creep
        ];
        // Sample densely enough that even a long crawl stays visually smooth.
        // ~60fps (was crawlMs/180 ≈ 5fps, which read as a stepped/choppy
        // creep during the slow bonus anticipation). Capped so an extra-long
        // crawl never builds an absurd keyframe count.
        const SAMPLES = Math.max(48, Math.min(600, Math.round(crawlMs / 16)));
        for (let s = 1; s <= SAMPLES; s++) {
          const tc = (crawlMs * s) / SAMPLES;   // time within the crawl (0..crawlMs)
          let pos;
          if (tc <= tLin) {
            pos = holdPx + v * tc;                                        // constant-velocity creep
          } else {
            const td = tc - tLin;                                        // into the decel glide
            pos = (holdPx + linearDist) + (v * td - 0.5 * (v / tDec) * td * td);
          }
          if (s === SAMPLES) pos = targetPx;    // guarantee the exact dead-stop landing
          kf.push({
            offset: Math.min(1, (engageTime + tc) / totalMs),
            transform: `translate3d(0, ${pos}px, 0)`,
            easing: 'linear',
          });
        }
        anim = stripEl.animate(kf, { duration: totalMs, fill: 'forwards' });

        // Blur: hi through the fast cruise, OFF the instant the crawl
        // begins (a near-still reel with blur looks like a smudge).
        const tBlurOff = setTimeout(() => {
          if (stripEl.isConnected) stripEl.classList.remove('blur-hi', 'blur-lo');
        }, Math.max(0, engageTime - 80));
        pendingTimers.push(tBlurOff);
      } else {
        // Smooth base motion — ONE fully continuous velocity profile:
        //   rest → (constant accel) → cruise at v → (constant decel) → dead stop.
        // Because velocity is continuous at every phase boundary (0 at both
        // ends, exactly v across the cruise joins), there is no kick at the
        // start OR the stop. Sampled at ~60fps into linear keyframes so the
        // compositor reproduces it exactly. This replaces the old profile
        // whose ease-out sped the reel back up at 90% (the choppy stop).
        totalMs = REEL_STOP_MS[r];
        const tA = Math.min(accelMs, totalMs * 0.18);       // accel time
        const tD = totalMs * 0.42;                          // long decel glide
        const tC = Math.max(0, totalMs - tA - tD);          // cruise time
        // scroll = 0.5*v*tA + v*tC + 0.5*v*tD  →  v = scroll / (totalMs - 0.5*(tA+tD))
        const v  = scroll / (totalMs - 0.5 * (tA + tD));    // peak (cruise) velocity, px/ms
        const dA = 0.5 * v * tA;                            // accel distance
        const dC = v * tC;                                  // cruise distance

        const kf = [];
        const SAMPLES = Math.max(48, Math.round(totalMs / 16));
        for (let s = 0; s <= SAMPLES; s++) {
          const t = (totalMs * s) / SAMPLES;
          let pos;
          if (t <= tA) {
            pos = startPx + 0.5 * (v / tA) * t * t;                       // accelerate 0→v
          } else if (t <= tA + tC) {
            pos = startPx + dA + v * (t - tA);                            // cruise at v
          } else {
            const td = t - tA - tC;                                      // decelerate v→0
            pos = startPx + dA + dC + (v * td - 0.5 * (v / tD) * td * td);
          }
          if (s === SAMPLES) pos = targetPx;                             // exact dead-stop landing
          kf.push({
            offset: s / SAMPLES,
            transform: `translate3d(0, ${pos}px, 0)`,
            easing: 'linear',
          });
        }
        anim = stripEl.animate(kf, { duration: totalMs, fill: 'forwards' });

        const tBlurLo = setTimeout(() => {
          if (stripEl.isConnected) { stripEl.classList.remove('blur-hi'); stripEl.classList.add('blur-lo'); }
        }, tA + tC * 0.6);
        pendingTimers.push(tBlurLo);
        const tBlurOff = setTimeout(() => {
          if (stripEl.isConnected) stripEl.classList.remove('blur-lo');
        }, tA + tC + tD * 0.4);
        pendingTimers.push(tBlurOff);
      }

      // Fires EXACTLY when the reel truly lands (hangs off anim.onfinish,
      // so it always matches the real landing — crawl or not).
      const onReelLand = () => {
        if (reelLanded[r]) return;
        reelLanded[r] = true;
        if (!reelEl.isConnected) return;
        // Per-reel stop "click" removed 2026-06 (operator) — was a ticking sound.

        // Landed BONUS on this reel — premium halo pulse + staged anticipation.
        try {
          const rows = [];
          (finalGrid[r] || []).forEach((sym, rowIdx) => { if (sym === 'BONUS') rows.push(rowIdx); });
          if (rows.length) {
            // Fire ONE hit (sound + visual pop) per duck on this reel,
            // slightly staggered + pitch-varied, so two ducks stacked on a
            // single reel read as two distinct bumps instead of one.
            rows.forEach((rowIdx, k) => {
              setTimeout(() => {
                // The landed viewport window is the strip's first 3 cells
                // (finalCol); target the imperative strip cell directly —
                // the React [data-testid=ld-cell-*] nodes aren't the ones
                // shown mid-animation.
                const cell = stripEl && stripEl.children[rowIdx];
                if (cell) {
                  cell.classList.remove('bonus-landed');
                  void cell.offsetWidth;                 // force reflow → replay the pop
                  cell.classList.add('bonus-landed');
                  setTimeout(() => cell.classList.remove('bonus-landed'), 900);
                }
                audioManager.play('bonus_symbol_land', { playbackRate: 0.97 + Math.random() * 0.12 });
              }, k * 150);
            });

            // Staged anticipation build — escalates as ducks accumulate.
            const prevSeen = bonusSeen;
            bonusSeen += rows.length;
            if (anticipationOn) {
              // 1st duck → soft, low rising whine (the wind-up begins).
              if (prevSeen < 1 && bonusSeen >= 1) {
                audioManager.startAnticipation({ dur: 3.4, f0: 100, f1: 420, peak: 0.010 });
              }
              // 2nd duck → the PEAK: step the whine UP (tenser) AND ring the
              // existing win cue to signal "you're one duck from BETO!",
              // building max suspense while the slowed deciding reel crawls.
              if (prevSeen < 2 && bonusSeen >= 2) {
                audioManager.startAnticipation({ dur: 4.6, f0: 150, f1: 620, peak: 0.020 });
                audioManager.play('win_medium');
                // "All eyes on that reel" — spotlight the reels still
                // crawling for the deciding 3rd duck, dim the finished ones.
                // Only when the slow crawl is actually engaged (2 ducks +
                // a reel still to come); never on a lone first duck.
                if (slowOn) {
                  spotlightActive = true;
                  for (let i = 0; i < REEL_COUNT; i++) {
                    const rEl = reelRefs.current[i];
                    if (!rEl) continue;
                    if (reelLanded[i]) { rEl.classList.add('reel-dimmed'); rEl.classList.remove('deciding-spotlight'); }
                    else rEl.classList.add('deciding-spotlight');
                  }
                }
              }
            }
          }
        } catch (_e) { /* presentation-only */ }

        // FIRST bonus just landed → the reels to its right are now
        // crawling.  Light the cabinet (the rising whine was already
        // started in the bonus-landed staging block above).
        if (anticipationOn && r === firstBonusReel) {
          try {
            const page = document.querySelector('.ld-page');
            if (page) { page.classList.remove('anticipation-1', 'anticipation-2'); page.classList.add('anticipation-3'); }
          } catch (_e) { /* presentation */ }
        }

        // Once the spotlight is live, each reel that lands drops out of the
        // spotlight and dims — narrowing focus onto the reels still crawling.
        if (spotlightActive) {
          const rEl = reelRefs.current[r];
          if (rEl) { rEl.classList.remove('deciding-spotlight'); rEl.classList.add('reel-dimmed'); }
        }

        // Final qualifying bonus stinger + cleanup on the last reel.
        if (r === REEL_COUNT - 1) {
          if (anticipationOn) {
            let totBonus = 0;
            finalGrid.forEach(col => col.forEach(s => { if (s === 'BONUS') totBonus++; }));
            // Hit = enough BONUS to advance/trigger → bright lift;
            // miss = soft downshift (no failure buzzer).
            audioManager.stopAnticipation(totBonus >= 3);
          }
          try {
            document.querySelector('.ld-page')?.classList.remove(
              'anticipation-1', 'anticipation-2', 'anticipation-3');
            spotlightActive = false;
            clearReelSpotlight();
          } catch (_e) { /* presentation */ }
        }
      };

      reelAnims.push(anim);
      reelPromises.push(new Promise(res => {
        anim.onfinish = () => {
          try { onReelLand(); } catch (_e) { /* audio/presentation must never stall the spin */ }
          stripEl.classList.remove('ld-spinning');  // restore resting drop-shadow
          // Zero-flash handoff — bake the final transform inline, then
          // cancel so no CSS change can shift the resting strip.
          try {
            anim.commitStyles();
            anim.cancel();
          } catch (_e) {
            stripEl.style.transform = `translate3d(0, ${targetPx}px, 0)`;
          }
          stripEl.style.transform = `translate3d(0, ${targetPx}px, 0)`;
          res();
        };
        // Safety net — totalMs already includes the full crawl, so a
        // modest cushion is enough.  onfinish is the true completion path.
        setTimeout(res, totalMs + 800);
      }));
    }

    // ── TAP-TO-STOP ───────────────────────────────────────────────────────
    // Let the player force the reels to land NOW. WAAPI .finish() jumps each
    // reel to its authoritative resting frame and fires the SAME onfinish
    // landing path (reel-stop audio + zero-flash handoff), so a manual stop
    // resolves EXACTLY like a natural landing — never a wrong/partial grid.
    stopSpinRef.current = () => {
      pendingTimers.forEach((id) => clearTimeout(id));
      reelAnims.forEach((a) => {
        try { if (a && (a.playState === 'running' || a.playState === 'paused')) a.finish(); }
        catch (_e) { /* ignore */ }
      });
    };
    // Small arming delay so a quick double-tap doesn't kill the spin the
    // instant it starts (reels get a beat to build up speed first).
    const tArm = setTimeout(() => setStoppable(true), 320);
    pendingTimers.push(tArm);

    Promise.all(reelPromises).then(async () => {
      stopSpinRef.current = null;
      setStoppable(false);
      document.removeEventListener('visibilitychange', onVisibleFinish);
      // Reel 5 (last reel) landed — fade the motion loop out cleanly so it
      // never lingers or cuts abruptly after the reels stop.
      audioManager.setLoopVolume('reel_spin_loop', 0, 90);
      setTimeout(() => audioManager.stopLoop('reel_spin_loop'), 100);
      clearReelSpotlight();
      pendingTimers.forEach(id => clearTimeout(id));

      // ZERO-FLASH HANDOFF —
      // The WAAPI animation ended with each strip translated so that its
      // TOP 3 cells (the authoritative `finalCol` symbols baked into the
      // sequence at spin start: `[final, ...fillers, ...currentTop3]`)
      // occupy the visible reel viewport.  Those top-3 <img> nodes have
      // been in the DOM continuously since spin start — no re-decode,
      // no reload, no repaint of pixels.
      //
      // Instead of rebuilding `innerHTML` (which would replace those
      // <img> nodes with fresh ones and cause a 1-frame decode flash),
      // we simply DROP the offscreen fillers + old-currentTop3 that
      // sit BELOW the clip-hidden `.ld-reel` viewport.  removeChild
      // touches ZERO visible pixels; the top-3 cells remain byte-for-
      // byte identical from the last animation frame into the idle
      // state.  This is the required "final animation frame == idle
      // frame" invariant.
      for (let r = 0; r < REEL_COUNT; r++) {
        const stripEl = stripRefs.current[r];
        if (!stripEl) continue;
        stripEl.classList.remove('blur-hi', 'blur-lo', 'ld-spinning');
        // Trim to exactly the 3 visible cells.  Bottom-up removal keeps
        // the top-of-strip children (indices 0-2) untouched — those are
        // the ones the player is currently seeing.  No innerHTML=, no
        // img.src rewrite, no React remount.
        while (stripEl.children.length > 3) {
          stripEl.removeChild(stripEl.lastChild);
        }
      }

      // Sync React state so the NEXT spin's `currentTop3 = grid[r]`
      // closure reads the authoritative outcome.  React's re-render
      // targets host fibers that were rendered before the imperative
      // strip mutation at spin start; their `stateNode.setAttribute`
      // calls become no-ops against the current live DOM — so this
      // `setGrid` does NOT trigger any visible repaint of the reel
      // cells.  It exists purely to keep game-state continuity.
      setGrid(finalGrid);
      // Casino-machine rhythm — a beat of silence between the final
      // reel settling and any win/feature presentation kicking in.
      await new Promise(rr => setTimeout(rr, reduceMotion ? 60 : 140));
      setSpinning(false);
      if (typeof document !== 'undefined') {
        document.querySelector('.ld-page')?.classList.remove('is-spinning');
      }
      resolve();
    });
  });

  // Evaluate autoplay stop conditions against the most recent spin. Returns
  // a human reason string if autoplay should stop, else null.
  const autoStopReason = () => {
    const cfg = autoConfigRef.current;
    const ls = lastSpinRef.current;
    if (cfg.stopOnFeature && ls.featureTriggered) return 'FEATURE HIT';
    if (cfg.stopOnBigWin && ls.wagerCredits > 0 && ls.winCredits / ls.wagerCredits >= 25) return 'BIG WIN';
    if (cfg.stopOnLowBalance && ls.balanceDollars <= cfg.lowBalanceDollars) return 'LOW BALANCE';
    if (creditsToDollars(credit) < creditsToDollars(wagerCredits)) return 'OUT OF CREDITS';
    return null;
  };

  // Autoplay chain — check ref after the whole flow to preserve intent.
  const chainAutoplay = () => {
    if (autoRef.current <= 0) return;
    const reason = autoStopReason();
    if (reason) {
      setAutoRemaining(0);
      setMessage(`AUTOPLAY STOPPED · ${reason}`);
      return;
    }
    setAutoRemaining(a => Math.max(0, a - 1));
    setTimeout(() => { if (autoRef.current > 0 && !featureActive) doSpin(); }, 600);
  };

  // Player-triggered STOP — force the currently spinning reels to land now.
  const stopReels = useCallback(() => {
    play('ui_button');
    stopSpinRef.current?.();
  }, []);

  const doSpin = useCallback(async () => {
    // Synchronous guard first — protects against React state async lag.
    if (spinInFlightRef.current || showFeatureIntro) return;
    if (!balanceReady) { setMessage('CONNECTING TO GAME SERVER…'); return; }
    if (featureActive) return;
    if (credit < wagerCredits) {
      setAutoRemaining(0);
      setMessage(credit < minBetCredits ? 'OUT OF CREDITS' : 'INSUFFICIENT CREDITS');
      return;
    }
    spinInFlightRef.current = true;
    try {
      setWin(0);
      setWinningCells(new Set());
      setWinningLines([]);
      setLineBoxes(null);
      setMessage('SPINNING…');

      // ── Server call (via platform WalletPort) ───────────────────────────
      let resp;
      ldBus.emit(LD_EVENTS.SPIN_STARTED, { wagerKey });
      try {
        resp = await ldWallet.placeSpin({ client_request_id: newRequestId(), wager_key: wagerKey });
      } catch (e) {
        const detail = e?.response?.data?.detail;
        setAutoRemaining(0);
        if (e?.response?.status === 402 && detail?.error === 'INSUFFICIENT_BALANCE') {
          setMessage('INSUFFICIENT CREDITS');
        } else if (e?.response?.status === 503) {
          setMessage('LUCKY DUCKY IS CURRENTLY CLOSED');
        } else {
          setMessage('SPIN FAILED · TRY AGAIN');
          console.error('lucky-ducky spin failed', e);
        }
        ldLog.error('spin_failed', { status: e?.response?.status });
        return;
      }

      // ── Animate base ────────────────────────────────────────────────────
      // If a 2-BONUS respin will follow, land the base spin cleanly (no search
      // crawl) so the player sees ONE search — the respin — not two.
      await animateGrid(resp.base.grid, { noAnticipation: !!resp.respin });
      highlightWins(resp.base.evaluation);
      if (resp.base.evaluation.total_win > 0) {
        setWin(resp.base.evaluation.total_win);
        audioManager.playWinBySize({
          winCredits: resp.base.evaluation.total_win, wagerCredits,
        });
        // Slight hold on a big base win so the celebration lands before the
        // next action / autoplay chains on — >= 20x fires the full BIG WIN
        // coin-burst; a mid win (15-20x) just gets a short beat.
        const baseRatio = resp.base.evaluation.total_win / Math.max(1, wagerCredits);
        if (baseRatio >= 20) await celebrateBigWin(resp.base.evaluation.total_win, wagerCredits);
        else if (baseRatio >= 15) await new Promise(r => setTimeout(r, 1800));
      }

      // ── Respin (server-decided) ────────────────────────────────────────
      if (resp.respin) {
        setRespinNotice(1);
        setPanelRespin({ bonus: resp.base.evaluation.bonus_count });
        setMessage('BONUS · RESPIN — DUCKS LOCKED!');
        audioManager.play('bonus_tease');
        await new Promise(r => setTimeout(r, 700));
        // B3: the reels that landed a BONUS on the base spin stay LOCKED
        // (frozen) — only the remaining reels respin, chasing a 3rd duck.
        const heldReels = (resp.base.grid || [])
          .map((col, i) => (col.includes('BONUS') ? i : -1))
          .filter((i) => i >= 0);
        await animateGrid(resp.respin.grid, { lockReels: heldReels, respinSlow: true });
        highlightWins(resp.respin.evaluation);
        if (resp.respin.evaluation.total_win > 0) {
          setWin(w => w + resp.respin.evaluation.total_win);
          audioManager.playWinBySize({
            winCredits: resp.respin.evaluation.total_win, wagerCredits,
          });
        }
        setRespinNotice(0);
        setPanelRespin(null);
      }

      // ── Feature (server-decided) ───────────────────────────────────────
      if (resp.feature) {
        setFeatureActive(true);
        setPanelRespin(null);
        setFreePlaysLeft(resp.feature.free_plays_awarded);
        setFeatureAwardedTotal(resp.feature.free_plays_awarded);
        setMessage(`BETO RESPINS! ${resp.feature.free_plays_awarded} FREE PLAYS · WINS PAY 3×`);
        // Reveal cue the instant the trigger lands, then a short beat so the
        // player registers "you hit it!" (board holds on the winning ducks)
        // before the feature intro appears — builds anticipation for the round.
        audioManager.play('beto_respins_enter');
        await new Promise(r => setTimeout(r, 4000));
        setShowFeatureIntro(true);
        // Duck the base-game tune out (so it never competes with the
        // feature bed) and start the looped BETO RESPINS feature music.
        audioManager.setLoopVolume('background_music', 0, 400);
        audioManager.loop('beto_music_start', { volume: 0.34 });

        // Root the entire feature presentation on a shared CSS state so
        // lighting / glow / gold-blue tint can flip on the .ld-page.
        // Intensity starts at 1 and escalates with retrigger events.
        const setFeatureClasses = (opts) => {
          if (typeof document === 'undefined') return;
          const page = document.querySelector('.ld-page');
          if (!page) return;
          page.classList.toggle('is-in-feature',  opts.inFeature);
          page.classList.toggle('intensity-1',    opts.intensity === 1);
          page.classList.toggle('intensity-2',    opts.intensity === 2);
          page.classList.toggle('intensity-3',    opts.intensity === 3);
          page.classList.toggle('final-spins',    !!opts.finalSpins);
        };
        let intensity = 1;
        setFeatureClasses({ inFeature: true, intensity, finalSpins: false });

        await new Promise(resolve => {
          featureContinueResolveRef.current = resolve;
          // Auto-progress the intro after ~2.2s so players who don't
          // tap START still enter the feature at a premium pace.
          setTimeout(() => {
            if (featureContinueResolveRef.current === resolve) {
              featureContinueResolveRef.current = null;
              resolve();
            }
          }, 2200);
        });
        setShowFeatureIntro(false);
        let runningFeatureWin = 0;
        let retriggerCount = 0;
        const plays = resp.feature.plays || [];
        const totalPlays = plays.length;
        // Live counter — tracks REMAINING free plays.  The backend already
        // played the ENTIRE feature (retrigger spins included) into `plays`,
        // so the counter must START at the initial award and grow by each
        // retrigger's +N as it lands — decrementing to EXACTLY 0 on the last
        // play.  (Starting at plays.length double-counted retriggers, which
        // left the counter showing spins remaining after the feature ended.)
        let remaining = resp.feature.free_plays_awarded;
        setFreePlaysLeft(remaining);

        for (let i = 0; i < plays.length; i++) {
          const p = plays[i];
          // Clear last play's win so the "WIN $x" banner only ever shows an
          // amount when THIS play actually won (a losing free play must not
          // keep flashing the previous play's amount).
          setWin(0);
          setWinMultiplier(3);
          // Final-spins presentation flip when only a few plays remain
          // BEFORE this one starts (helps music/riser wind up early).
          const remainingBeforeThis = remaining;
          const finalSpins = remainingBeforeThis <= 3;
          if (finalSpins) audioManager.play('beto_music_final_spins');
          setFeatureClasses({ inFeature: true, intensity, finalSpins });

          // Reel-motion audio OFF during feature spins too — feature music
          // continues underneath; celebration cues fire after the land.
          // Feature reels run ~15% faster than base game.  The animation
          // still accelerates / cruises / decelerates smoothly and lands
          // directly on the authoritative final symbols — the zero-flash
          // stop invariants from the base game apply unchanged.
          // If this feature spin is about to land another qualifying bonus
          // (a retrigger), cut the feature music and slow the reels so the
          // "here comes another duck!" moment reads clean and suspenseful.
          const willRetrigger = (p.respin ? p.respin.evaluation.bonus_count : p.evaluation.bonus_count) >= 3;
          if (willRetrigger) audioManager.setLoopVolume('beto_music_start', 0.0, 200);
          await animateGrid(p.grid, { speedMultiplier: willRetrigger ? 1.15 : 0.85 });
          highlightWins(p.evaluation);

          // B3 in-feature: 2 BONUS during a free play → lock those reels and
          // respin the rest once (same as the base game). A 3rd duck retriggers.
          if (p.respin) {
            await new Promise(r => setTimeout(r, 350));
            setMessage('BONUS · RESPIN — DUCKS LOCKED!');
            audioManager.play('bonus_tease');
            const fHeld = (p.grid || []).map((c, idx) => (c.includes('BONUS') ? idx : -1)).filter(idx => idx >= 0);
            await animateGrid(p.respin.grid, { lockReels: fHeld, respinSlow: true });
            highlightWins(p.respin.evaluation);
            if (p.respin.evaluation.total_win > 0) {
              setWin(p.respin.evaluation.total_win);
              runningFeatureWin += p.respin.evaluation.total_win;
              setTotalFeatureWin(runningFeatureWin);
              setMultReveal({ n: 3, ts: Date.now() });
              audioManager.playWinBySize({ winCredits: p.respin.evaluation.total_win, wagerCredits });
            }
          }

          // Bonus land presentation: any BONUS on-screen this feature
          // spin gets a premium glow via `.ld-reel-cell.bonus-landed`.
          try {
            document.querySelectorAll('.ld-reel-cell.bonus-landed')
              .forEach(el => el.classList.remove('bonus-landed'));
            p.grid.forEach((col, reelIdx) => {
              col.forEach((sym, rowIdx) => {
                if (sym === 'BONUS') {
                  const cell = document.querySelector(
                    `[data-testid="ld-cell-${reelIdx}-${rowIdx}"]`
                  );
                  if (cell) {
                    cell.classList.add('bonus-landed');
                    setTimeout(() => cell.classList.remove('bonus-landed'), 900);
                  }
                }
              });
            });
            if (p.evaluation.bonus_count >= 3) {
              audioManager.play('feature_bonus_land');
              // Bring the feature music back in after the bonus reveal.
              setTimeout(() => audioManager.setLoopVolume('beto_music_start', 0.30, 500), 800);
            }
          } catch (_e) { /* presentation-only, never blocks */ }

          if (p.evaluation.total_win > 0) {
            setWin(p.evaluation.total_win);
            runningFeatureWin += p.evaluation.total_win;
            setTotalFeatureWin(runningFeatureWin);
            // Duck the feature music under the win celebration, then lift
            // it back to full energy after the payout reads out.
            audioManager.setLoopVolume('beto_music_start', 0.1, 120);
            audioManager.playWinBySize({
              winCredits: p.evaluation.total_win, wagerCredits,
            });
            setTimeout(() => audioManager.setLoopVolume('beto_music_start', 0.30, 450), 1300);
            // Multiplier reveal — always ×3 during BETO RESPINS.
            setMultReveal({ n: 3, ts: Date.now() });
            audioManager.play('multiplier_x3_reveal');
            audioManager.play('win_countup');
            // Size-tier feature win hooks for premium music/sfx layers.
            const ratio = p.evaluation.total_win / Math.max(1, wagerCredits);
            if      (ratio >= 40) audioManager.play('feature_big_win');
            else if (ratio >= 10) audioManager.play('feature_medium_win');
            else                  audioManager.play('feature_small_win');
            if (winMultiplier > 1) audioManager.play('feature_multiplier');
          }

          // Retrigger award — server tells us exactly how many free plays
          // this spin added (0 if no retrigger).  We use it to (a) animate
          // the +N delta, (b) bump intensity, (c) fire the extra-spins
          // audio hook.  This is display-only; the math is server-authored.
          remaining -= 1;
          const award = (p.evaluation && p.evaluation.retrigger_award) || 0;
          if (award > 0) {
            retriggerCount += 1;
            audioManager.play('feature_retrigger');
            audioManager.play('feature_extra_spins_awarded');
            audioManager.play('beto_retrigger');
            // Intensity: L1 → L2 on any retrigger, L2 → L3 on 4-of-a-kind+
            //           or when award ≥ 15 (a "large" retrigger).
            const large = (p.evaluation.bonus_count >= 4) || (award >= 15);
            intensity = large ? 3 : Math.min(3, Math.max(2, intensity));
            audioManager.play('beto_music_intensity_up');
            // Animate the counter: show the "+N" delta briefly, then
            // update the base counter to the post-award value.
            setFreePlaysDelta({ n: award, large, ts: Date.now() });
            remaining += award;
            setFeatureAwardedTotal(t => t + award);
            setFeatureClasses({ inFeature: true, intensity, finalSpins });
          }
          if (i === plays.length - 1) audioManager.play('feature_final_spin');
          setFreePlaysLeft(remaining);
          setMessage(`FREE PLAY ${i + 1} / ${totalPlays} · BONUS WIN ${fmtDollars(runningFeatureWin)}`);
          // Hold longer after a big feature win so the player savours it;
          // >= 20x fires the full BIG WIN coin-burst celebration.
          const fWinRatio = p.evaluation.total_win / Math.max(1, wagerCredits);
          if (fWinRatio >= 20) {
            await celebrateBigWin(p.evaluation.total_win, wagerCredits);
          } else {
            await new Promise(r => setTimeout(r, fWinRatio >= 10 ? 1600 : 350));
          }
        }
        setFeatureActive(false);
        setWinMultiplier(1);
        setFeatureClasses({ inFeature: false, intensity: 0, finalSpins: false });
        audioManager.stopLoop('beto_music_start');
        audioManager.play('beto_music_end');
        audioManager.play('feature_complete');
        audioManager.play('feature_total_win');
        // Restore the base-game background tune (was ducked to 0 on entry).
        audioManager.loop('background_music', { volume: 0.18 });
        audioManager.setLoopVolume('background_music', 0.18, 600);
        // Show the recap overlay for a beat before returning to the base
        // game.  Auto-dismisses after 6s; player may also tap CONTINUE.
        setFeatureRecap({
          win: resp.feature.total_win,
          plays: totalPlays,
          retriggers: retriggerCount,
        });
        setShowFeatureComplete(true);
        if (featureCompleteTimerRef.current) clearTimeout(featureCompleteTimerRef.current);
        featureCompleteTimerRef.current = setTimeout(() => setShowFeatureComplete(false), 6000);
        setMessage(`BETO RESPINS COMPLETE · TOTAL ${fmtDollars(resp.feature.total_win)}`);
      }

      // ── Balance sync (authoritative) ───────────────────────────────────
      setCredit(dollarsToCredits(resp.balance_after_dollars));
      // Shared progressive jackpot hit — celebrate after the reels/feature.
      if (resp.jackpot) setJackpotWin(resp.jackpot);
      ldBus.emit(LD_EVENTS.BALANCE_CHANGED, { balanceDollars: resp.balance_after_dollars });
      ldBus.emit(LD_EVENTS.SPIN_SETTLED, {
        winCredits: resp.total_win_credits || 0,
        featureTriggered: !!resp.feature,
      });
      ldLog.event('spin_settled', {
        wagerKey,
        winCredits: resp.total_win_credits || 0,
        featureTriggered: !!resp.feature,
        balanceAfter: resp.balance_after_dollars,
      });
      // Snapshot this spin for autoplay stop-condition evaluation.
      lastSpinRef.current = {
        featureTriggered: !!resp.feature,
        winCredits: resp.total_win_credits || 0,
        wagerCredits,
        balanceDollars: resp.balance_after_dollars,
      };
      // Near-miss teaser: when the base spin lands exactly 2 bonus ducks (the
      // moment the 3rd-duck chase begins) and the feature did NOT ultimately
      // trigger, flash a "heating up" hint. ~8% of spins — keeps the chase alive.
      if (!resp.feature) {
        const baseBonus = resp.base.grid.flat().filter(s => s === 'BONUS').length;
        if (baseBonus === 2) {
          setShowTeaser(true);
          if (teaserTimerRef.current) clearTimeout(teaserTimerRef.current);
          teaserTimerRef.current = setTimeout(() => setShowTeaser(false), 2000);
        }
      }
      if (!resp.feature && !resp.respin) {
        setMessage(resp.total_win_credits > 0
          ? `YOU WON ${fmtDollars(resp.total_win_credits)}`
          : 'GOOD LUCK · TAP SPIN');
      }
    } finally {
      spinInFlightRef.current = false;
    }
    chainAutoplay();
  }, [showFeatureIntro, balanceReady, featureActive, credit, wagerCredits, wagerKey]);

  // Keep a live ref so host/hardware events (SPIN_BUTTON) always invoke the
  // latest doSpin without re-subscribing every render.
  const doSpinRef = useRef(doSpin);
  doSpinRef.current = doSpin;

  // Platform event-bus wiring — standardized input behaves identically for the
  // on-screen SPIN tap (online) and a future physical spin button (kiosk).
  // The peripheral handlers (CREDIT_ADDED / CASHOUT_REQUEST / DEVICE_ERROR)
  // never fire online; they establish the standard so hardware plugs in later
  // with zero game changes.
  useEffect(() => {
    const offSpin = ldBus.on(LD_EVENTS.SPIN_BUTTON, () => { if (doSpinRef.current) doSpinRef.current(); });
    const offCredit = ldBus.on(LD_EVENTS.CREDIT_ADDED, async () => {
      try { const bal = await ldWallet.getBalance(); setCredit(dollarsToCredits(bal)); } catch (_e) { /* ignore */ }
    });
    const offCashout = ldBus.on(LD_EVENTS.CASHOUT_REQUEST, () => { ldLog.event('cashout_request'); });
    const offDevErr = ldBus.on(LD_EVENTS.DEVICE_ERROR, (d) => ldLog.error('device_error', d));
    return () => { offSpin(); offCredit(); offCashout(); offDevErr(); };
  }, []);

  const startFeatureSpins = () => {
    // Resolve the pending doSpin promise so its feature-plays loop can run.
    if (featureContinueResolveRef.current) {
      const r = featureContinueResolveRef.current;
      featureContinueResolveRef.current = null;
      r();
    } else {
      setShowFeatureIntro(false);
    }
  };

  // Reactive environment class — win / big-win / feature triggers subtle
  // ambient responses (glow, particle boost) that fade automatically.
  // Never runs before the authoritative server result is known.
  const [envState, setEnvState] = useState('idle');  // idle | win | big-win | feature
  const envResetRef = useRef(null);

  const pulseEnv = useCallback((state, holdMs = 1600) => {
    if (envResetRef.current) clearTimeout(envResetRef.current);
    setEnvState(state);
    envResetRef.current = setTimeout(() => setEnvState('idle'), holdMs);
  }, []);

  // Hook environment responses to win/feature state.
  useEffect(() => {
    if (featureActive) { setEnvState('feature'); return; }
    // Only when spin is complete (not spinning) and a win is present.
    if (!spinning && win > 0 && wagerCredits > 0) {
      const ratio = win / wagerCredits;
      pulseEnv(ratio >= 15 ? 'big-win' : 'win', ratio >= 15 ? 2600 : 1400);
    }
  }, [win, spinning, featureActive, wagerCredits, pulseEnv]);

  return (
    <div
      className={`ld-page env-${envState}`}
      data-testid="lucky-ducky-page"
      style={{
        backgroundImage: `radial-gradient(1200px 600px at 50% -20%, rgba(255,210,0,0.15), transparent 60%), url("${ART.environment}")`,
        backgroundSize: 'auto, cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        // Premium decor asset URLs exposed as CSS custom properties so
        // the CSS layer can reference them via var(...) — this bypasses
        // webpack css-loader's module-import resolution of url()s that
        // start with `/` and treats them correctly as public-folder
        // absolute URLs at runtime.
        '--ld-hero':         'url("/games/luckyDucky/decor/LD_DUCK_HERO.png")',
        '--ld-tube':         'url("/games/luckyDucky/decor/LD_DUCK_TUBE.png")',
        '--ld-coin':         'url("/games/luckyDucky/decor/LD_COIN_SINGLE.png")',
        '--ld-bill':         'url("/games/luckyDucky/decor/LD_BILL_SINGLE.png")',
        '--ld-lightrays':    'url("/games/luckyDucky/decor/LD_LIGHT_RAYS.png")',
        '--ld-water-ripple': 'url("/games/luckyDucky/decor/LD_WATER_RIPPLE.png")',
      }}
    >
      {/* Portrait-only lock — a CSS media query reveals this full-screen
          overlay when a phone is rotated to landscape (short viewport).
          Desktop/tablet landscape is untouched. */}
      <div className="ld-rotate-gate" data-testid="ld-rotate-gate" aria-hidden="true">
        <div className="ld-rotate-icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="7" y="2" width="10" height="20" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
            <line x1="7" y1="18.5" x2="17" y2="18.5" stroke="currentColor" strokeWidth="1.4" />
          </svg>
        </div>
        <div className="ld-rotate-title">Rotate your device</div>
        <div className="ld-rotate-sub">Lucky Ducky plays best in portrait</div>
      </div>

      {loading && (
        <div className="ld-loading" data-testid="ld-loading">
          <div className="ld-loading-inner">
            <img
              src="/games/luckyDucky/decor/LD_LOGO_MAIN.png"
              alt="Lucky Ducky"
              className="ld-loading-logo"
              draggable={false}
            />
            <div className="ld-loading-text">Lucky Ducky loading…</div>
            <div className="ld-loading-bar" aria-hidden="true"><span /></div>
            <div className="ld-loading-powered">powered by <b>BETohalics</b></div>
          </div>
        </div>
      )}

      {bigWin && (
        <div className={`ld-bigwin ${bigWin.label.replace(' ', '-').toLowerCase()}`} data-testid="ld-bigwin">
          <div className="ld-bigwin-coins" aria-hidden="true">
            {Array.from({ length: 16 }).map((_, i) => (
              <span key={i} className="ld-coin" style={{ '--i': i, '--a': `${(360 / 16) * i}deg` }} />
            ))}
          </div>
          <div className="ld-bigwin-inner">
            <div className="ld-bigwin-title" data-testid="ld-bigwin-title">{bigWin.label}</div>
            <div className="ld-bigwin-amount" data-testid="ld-bigwin-amount">{fmtDollars(bigWin.display)}</div>
          </div>
        </div>
      )}
      {/* Ambient underwater atmosphere — layered depth.  All movement uses
          GPU-friendly transform/opacity only.  prefers-reduced-motion
          and `.is-spinning` both throttle these layers via CSS so the
          reels always own the compositor budget. */}
      <div className="ld-ambience" aria-hidden="true">
        {/* Supplied premium light-ray overlay — subtle upper-scene wash
            to blend depth with the existing water background. */}
        <div className="ld-lightrays" aria-hidden="true" />
        <div className="ld-caustics" />
        <div className="ld-bubbles">
          {Array.from({ length: 14 }).map((_, i) => (
            <span key={i} className={`ld-bubble b-${i}`} />
          ))}
        </div>
        {/* SURFACE LAYER — Two supporting ducks that create hierarchy under
            the dominant centered LUCKY DUCKY logo:
              sd-0 = HERO duck  — bottom-right corner accent
              sd-1 = TUBE duck  — small upper-left accent, clears LOBBY btn
            Each duck sits on a water-ripple base so it blends into the
            scene rather than looking like a pasted sticker. */}
        <div className="ld-surface-life">
          {[0, 1].map((i) => (
            <span key={i} className={`ld-surface-duck sd-${i}`} data-testid={`ld-duck-${i}`}>
              <span className="ld-duck-water" aria-hidden="true" />
              <span className="ld-ripple" />
              <span className="ld-ripple ld-ripple-2" />
            </span>
          ))}
        </div>
        {/* PREMIUM CASINO ACCENTS — coins scattered around the top with
            a couple of floating dollar bills.  Never over the reels. */}
        <div className="ld-coin-accents">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <span key={i} className={`ld-coin c-${i}`} />
          ))}
          <span className="ld-bill" />
          <span className="ld-bill ld-bill-2" />
        </div>
        {/* FRONT / AMBIENT LAYER — subtle foreground light glints. */}
        <div className="ld-front-glints">
          <span className="ld-glint g-1" />
          <span className="ld-glint g-2" />
          <span className="ld-glint g-3" />
        </div>
        {/* WIN BURST — a small bubble surge, only visible when
            .ld-page.env-win / .env-big-win is set. */}
        <div className="ld-win-burst">
          {Array.from({ length: 8 }).map((_, i) => (
            <span key={i} className={`ld-burst-bubble wb-${i}`} />
          ))}
        </div>
      </div>
      <div className="ld-shell" data-testid="ld-shell">
        {/* TOP BAR */}
        <div className="ld-topbar">
          <button className="ld-back-btn" onClick={() => { play('ui_button'); navigate('/casino/slots'); }} data-testid="ld-back">← LOBBY</button>
          <button
            className="ld-paylines-badge"
            onClick={() => { play('ui_button'); setPaytableTab('PAYS'); setPaytableOpen(true); }}
            data-testid="ld-paytable-btn"
          >PAYTABLE</button>
          <button
            className={`ld-feature-panel${featureActive ? ' is-active' : ''}${!featureActive && showFeatureComplete ? ' is-complete' : ''}${!featureActive && !showFeatureComplete && panelRespin ? ' is-respin' : ''}`}
            onClick={() => { play('ui_button'); setPaytableTab('BETO RESPINS'); setPaytableOpen(true); }}
            data-testid="ld-feature-badge"
            aria-label="BETO RESPINS feature status — tap for info"
          >
            <span className="ld-fp-duck-wrap">
              <img src={SYMBOLS.BONUS.asset} className="ld-fp-duck" alt="Bonus Duck" draggable={false} />
            </span>
            <span className="ld-fp-text">
            <span className="ld-fp-title">BETO RESPINS</span>
            {featureActive ? (
              <>
                <span className="ld-fp-main" data-testid="ld-fp-freeplays">FREE PLAYS {freePlaysLeft} / {featureAwardedTotal}</span>
                <span className="ld-fp-total" data-testid="ld-fp-bonus-total">WIN {fmtDollars(totalFeatureWin)}</span>
                <span className="ld-fp-sub" data-testid="ld-fp-mult">{(winMultiplier > 1 ? winMultiplier : RULES.feature_win_multiplier)}× WINS</span>
              </>
            ) : showFeatureComplete ? (
              <span className="ld-fp-main" data-testid="ld-fp-complete">COMPLETE</span>
            ) : panelRespin ? (
              <>
                <span className="ld-fp-main" data-testid="ld-fp-respin">BONUS {panelRespin.bonus} / {RULES.min_bonus_to_award_free_plays}</span>
                <span className="ld-fp-sub">RESPIN FREE SPIN</span>
              </>
            ) : (
              <>
                <span className="ld-fp-main">FREE PLAYS</span>
                <span className="ld-fp-sub ld-fp-tiers" data-testid="ld-fp-tiers">
                  3+ = 15
                </span>
              </>
            )}
            </span>
          </button>
        </div>

        {/* BOARD */}
        <div className="ld-board-wrap">
          {/* LUCKY DUCKY logo — dead-centered over the play board, sitting
              just above the reels. */}
          <div className="ld-logo-wrap">
            <img src="/games/luckyDucky/decor/LD_LOGO_MAIN.png" alt="Lucky Ducky" className="ld-logo" data-testid="ld-logo" />
          </div>
          <JackpotStrip className="ld-jackpot-strip" />
          {win > 0 && !spinning && (
            <div className="ld-win-banner" data-testid="ld-win-banner">
              WIN {fmtDollars(win)}{featureActive && winMultiplier > 1 ? ` · ×${winMultiplier}` : ''}
            </div>
          )}
          {respinNotice > 0 && (
            <div className="ld-win-banner" data-testid="ld-respin-banner" style={{ background: 'linear-gradient(180deg,#4bd0ff 0%,#054f9b 100%)', color: '#fff' }}>
              RESPIN FREE SPIN
            </div>
          )}
          <div className="ld-board" data-testid="ld-board" ref={boardRef}>
            {win > 0 && !spinning && (
              <div className="ld-win-rollup" data-testid="ld-win-rollup" aria-hidden="true">
                {fmtDollars(rollup)}
              </div>
            )}
            {lineBoxes && !spinning && lineBoxes.boxes.length > 0 && (
              <svg
                className="ld-winlines"
                viewBox={`0 0 ${lineBoxes.w} ${lineBoxes.h}`}
                preserveAspectRatio="none"
                data-testid="ld-winlines"
                aria-hidden="true">
                {lineBoxes.lines.map((L, i) => (
                  <polyline key={`p${i}`} points={L.points} className="ld-winline-path" style={{ '--wl': L.color }} />
                ))}
                {lineBoxes.boxes.map((b, j) => (
                  <rect
                    key={`b${j}`}
                    x={b.x + 3} y={b.y + 3}
                    width={Math.max(0, b.w - 6)} height={Math.max(0, b.h - 6)}
                    rx="10"
                    className="ld-winline-box" />
                ))}
              </svg>
            )}
            {[0, 1, 2, 3, 4].map(reelIdx => (
              <div
                key={reelIdx}
                className="ld-reel"
                data-testid={`ld-reel-${reelIdx}`}
                ref={(el) => { reelRefs.current[reelIdx] = el; }}>
                <div
                  className="ld-reel-strip"
                  ref={(el) => { stripRefs.current[reelIdx] = el; }}>
                  {grid[reelIdx].map((symId, rowIdx) => {
                    const s = SYMBOLS[symId];
                    const key = `${reelIdx}-${rowIdx}`;
                    const isWin = !spinning && winningCells.has(key);
                    return (
                      <div
                        key={key}
                        className={`ld-reel-cell${isWin ? ' win' : ''}${symId === 'BONUS' ? ' is-bonus' : ''}${symId === 'WILD' ? ' is-wild' : ''}`}
                        data-sym={symId}
                        data-testid={`ld-cell-${reelIdx}-${rowIdx}`}>
                        {s.composite ? (
                          <div className="ld-composite">
                            <img className="ld-comp-bell" src={s.bell} alt="" draggable={false} />
                            <img className="ld-comp-bar" src={s.bar} alt={s.label} draggable={false} />
                          </div>
                        ) : (
                          <img src={s.asset} alt={s.label} draggable={false} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* HUD — player-facing values are formatted dollars; underlying
             state (`credit`, `wagerCredits`, `win`) remains integer credits
             so arithmetic never touches floating-point money. */}
        <div className="ld-hud" data-testid="ld-hud">
          <div className="ld-meter" data-testid="ld-credit">
            <span className="lbl">BALANCE</span>
            <span className="val">{fmtDollars(credit)}</span>
          </div>
          <div className="ld-bet" data-testid="ld-bet">
            <button onClick={() => cycleWager(-1)} disabled={spinning || featureActive || wagerKey === WAGER_KEYS[0]} data-testid="ld-bet-minus">−</button>
            <div className="bet-body">
              <span className="lbl">BET</span>
              <span className="val">{fmtDollars(wagerCredits)}</span>
            </div>
            <button onClick={() => cycleWager(1)} disabled={spinning || featureActive || wagerKey === WAGER_KEYS[WAGER_KEYS.length - 1]} data-testid="ld-bet-plus">+</button>
          </div>
          <div className="ld-meter win" data-testid="ld-win" style={{ position: 'relative' }}>
            <span className="lbl">WIN</span>
            <span className="val">{fmtDollars(win)}</span>
            {multReveal && (
              <span
                key={multReveal.ts}
                className="ld-mult-reveal"
                data-testid="ld-mult-reveal"
                onAnimationEnd={() => setMultReveal(null)}
              >
                ×{multReveal.n}
              </span>
            )}
          </div>
          <div className="ld-meter mult" data-testid="ld-multiplier">
            <span className="lbl">MULT</span>
            <span className="val">×{winMultiplier}</span>
          </div>
          <div className="ld-meter" data-testid="ld-freeplays">
            <span className="lbl">FREE PLAYS</span>
            <span className="val">{freePlaysLeft}</span>
            {freePlaysDelta && (
              <span
                key={freePlaysDelta.ts}
                className={`ld-fp-delta${freePlaysDelta.large ? ' large' : ''}`}
                data-testid="ld-freeplays-delta"
                onAnimationEnd={() => setFreePlaysDelta(null)}
              >
                +{freePlaysDelta.n}
              </span>
            )}
          </div>
          <div className="ld-actions">
            <button className="ld-btn max"  onClick={maxBet} disabled={spinning || featureActive || outOfCredits} data-testid="ld-max">MAX</button>
            <button className="ld-btn auto" onClick={() => { play('ui_button'); if (autoRemaining > 0) { setAutoRemaining(0); } else { setAutoPanelOpen(true); } }} disabled={spinning || featureActive || outOfCredits} data-testid="ld-auto">
              {autoRemaining > 0 ? `STOP · ${autoRemaining}` : 'AUTO'}
            </button>
            <button className="ld-btn help"  onClick={() => { play('ui_button'); setPaytableTab('RULES'); setPaytableOpen(true); }} data-testid="ld-help">HELP</button>
            <button className="ld-btn sound" onClick={toggleMute} data-testid="ld-sound">{soundOn ? 'SND ON' : 'SND OFF'}</button>
            <button className={`ld-btn spin${spinning ? ' spinning' : ''}${spinning && stoppable ? ' stoppable' : ''}`} onClick={() => { if (spinning) { if (stoppable) stopReels(); } else { ldBus.emit(LD_EVENTS.SPIN_BUTTON); } }} disabled={(spinning && !stoppable) || showFeatureIntro || outOfCredits} data-testid="ld-spin">
              {spinning ? (stoppable ? 'STOP' : 'SPINNING…') : featureActive ? 'FREE SPIN' : outOfCredits ? 'NO CREDITS' : 'SPIN'}
            </button>
          </div>
        </div>

        {autoRemaining > 0 && (
          <div className="ld-auto-quickbar" data-testid="ld-auto-quickbar">
            <span className="ld-auto-chip count" data-testid="ld-auto-quickbar-count">▶ AUTOPLAY · {autoRemaining} LEFT</span>
            {autoConfig.stopOnFeature && <span className="ld-auto-chip">STOPS ON FEATURE</span>}
            {autoConfig.stopOnBigWin && <span className="ld-auto-chip">STOPS ≥25× WIN</span>}
            {autoConfig.stopOnLowBalance && <span className="ld-auto-chip">STOPS ≤ ${autoConfig.lowBalanceDollars}</span>}
            <button className="ld-auto-chip stop" onClick={() => { play('ui_button'); setAutoRemaining(0); }} data-testid="ld-auto-quickbar-stop">STOP</button>
          </div>
        )}

        {showTeaser && (
          <div className="ld-teaser" data-testid="ld-teaser">
            <span className="ld-teaser-flame" aria-hidden="true" />
            BONUS HEATING UP · 1 MORE DUCK!
            <span className="ld-teaser-flame" aria-hidden="true" />
          </div>
        )}

        <div className="ld-message" data-testid="ld-message">{message}</div>
      </div>

      {showFeatureIntro && (
        <div className="ld-feature-intro" data-testid="ld-feature-intro">
          <div className="ld-feature-intro-title">BETO RESPINS</div>
          <div className="ld-feature-intro-sub">Every win pays 3×</div>
          <div className="ld-feature-intro-stats">
            <div className="ld-feature-intro-stat">
              <span className="val" data-testid="ld-intro-free-plays">15</span>
              <span className="lbl">FREE PLAYS</span>
            </div>
            <div className="ld-feature-intro-stat">
              <span className="val" data-testid="ld-intro-mult">×3</span>
              <span className="lbl">MULTIPLIER</span>
            </div>
          </div>
          <button className="ld-btn spin ld-feature-continue" style={{ marginTop: 28 }} onClick={startFeatureSpins} data-testid="ld-feature-start">START FREE PLAYS</button>
        </div>
      )}

      {showFeatureComplete && (
        <div className="ld-feature-complete" data-testid="ld-feature-complete">
          <div className="ld-feature-complete-coins" aria-hidden="true">
            {Array.from({ length: 14 }).map((_, i) => (
              <span key={i} className="ld-coin" style={{ '--i': i, '--a': `${(360 / 14) * i}deg` }} />
            ))}
          </div>
          <div className="ld-feature-complete-title">BETO RESPINS COMPLETE</div>
          <div className="ld-feature-complete-amount" data-testid="ld-feature-complete-amount">
            {fmtDollars(featureRecap.win || 0)}
          </div>
          <div className="ld-feature-complete-sub">TOTAL FEATURE WIN</div>
          <div className="ld-recap-stats" data-testid="ld-recap-stats">
            <div className="ld-recap-stat">
              <span className="val" data-testid="ld-recap-plays">{featureRecap.plays || 0}</span>
              <span className="lbl">FREE PLAYS</span>
            </div>
            <div className="ld-recap-stat">
              <span className="val" data-testid="ld-recap-retriggers">{featureRecap.retriggers || 0}</span>
              <span className="lbl">RETRIGGERS</span>
            </div>
          </div>
          <button className="ld-btn max" style={{ marginTop: 22 }} onClick={() => { play('ui_button'); if (featureCompleteTimerRef.current) clearTimeout(featureCompleteTimerRef.current); setShowFeatureComplete(false); }} data-testid="ld-feature-complete-close">CONTINUE</button>
        </div>
      )}

      {autoPanelOpen && (
        <div className="ld-auto-modal" data-testid="ld-auto-panel" onClick={() => setAutoPanelOpen(false)}>
          <div className="ld-auto-card" onClick={e => e.stopPropagation()}>
            <div className="ld-auto-head">
              <h2>AUTOPLAY</h2>
              <button className="ld-pt-close" onClick={() => setAutoPanelOpen(false)} data-testid="ld-auto-close" aria-label="Close">✕</button>
            </div>
            <div className="ld-auto-section">
              <div className="ld-auto-label">NUMBER OF SPINS</div>
              <div className="ld-auto-counts">
                {[10, 25, 50, 100].map(n => (
                  <button
                    key={n}
                    className={`ld-auto-count${autoConfig.count === n ? ' active' : ''}`}
                    onClick={() => { play('ui_button'); setAutoConfig(c => ({ ...c, count: n })); }}
                    data-testid={`ld-auto-count-${n}`}
                  >{n}</button>
                ))}
              </div>
            </div>
            <div className="ld-auto-section">
              <div className="ld-auto-label">STOP AUTOPLAY IF…</div>
              <label className="ld-auto-toggle" data-testid="ld-auto-stop-feature">
                <input type="checkbox" checked={autoConfig.stopOnFeature} onChange={e => setAutoConfig(c => ({ ...c, stopOnFeature: e.target.checked }))} />
                <span>BETO Respins triggers</span>
              </label>
              <label className="ld-auto-toggle" data-testid="ld-auto-stop-bigwin">
                <input type="checkbox" checked={autoConfig.stopOnBigWin} onChange={e => setAutoConfig(c => ({ ...c, stopOnBigWin: e.target.checked }))} />
                <span>A single win is 25× the bet or more</span>
              </label>
              <label className="ld-auto-toggle" data-testid="ld-auto-stop-lowbal">
                <input type="checkbox" checked={autoConfig.stopOnLowBalance} onChange={e => setAutoConfig(c => ({ ...c, stopOnLowBalance: e.target.checked }))} />
                <span>Balance drops to
                  <input
                    type="number" min="0" step="1" className="ld-auto-thresh"
                    value={autoConfig.lowBalanceDollars}
                    onClick={e => e.stopPropagation()}
                    onChange={e => setAutoConfig(c => ({ ...c, lowBalanceDollars: Math.max(0, parseFloat(e.target.value) || 0) }))}
                    data-testid="ld-auto-lowbal-input"
                  /> or less
                </span>
              </label>
            </div>
            <button
              className="ld-btn spin ld-auto-start"
              onClick={() => { play('ui_button'); setAutoPanelOpen(false); setAutoRemaining(autoConfig.count); setTimeout(() => doSpin(), 50); }}
              data-testid="ld-auto-start"
            >START · {autoConfig.count} SPINS</button>
          </div>
        </div>
      )}

      {outOfCredits && !spinning && !featureActive && !showFeatureComplete && (
        <div className="ld-nocredit-overlay" data-testid="ld-out-of-credits">
          <div className="ld-nocredit-card">
            <img src="/games/lucky-ducky/LD-01-mascot.png" alt="" className="ld-nocredit-duck" aria-hidden="true" />
            <div className="ld-nocredit-title">OUT OF CREDITS</div>
            <div className="ld-nocredit-sub">Your balance is too low to play. Load $20+ to earn a Free Play bonus, or ask your cashier to reload your account.</div>
            <button className="ld-btn spin" onClick={() => { play('ui_button'); navigate('/casino/slots'); }} data-testid="ld-nocredit-lobby">BACK TO LOBBY</button>
          </div>
        </div>
      )}

      {showHowTo && (
        <div className="ld-howto-modal" data-testid="ld-howto" onClick={dismissHowTo}>
          <div className="ld-howto-card" onClick={e => e.stopPropagation()}>
            <img src="/games/lucky-ducky/LD-16-logo.png" alt="Lucky Ducky" className="ld-howto-logo" />
            <div className="ld-howto-title">HOW TO PLAY</div>
            <ul className="ld-howto-list">
              <li><b>Pick your bet</b> with − / + and tap <b>SPIN</b>. All 20 paylines are always active.</li>
              <li><b>Match 3+</b> symbols left-to-right from reel 1 to win. Higher bets pay proportionally more.</li>
              <li><b>Double Wild</b> substitutes for everything except Bonus and pays <b>2×</b> on winning lines.</li>
              <li>Land <b>3+ Bonus ducks</b> anywhere to trigger <b>BETO RESPINS</b> — free plays where every win pays <b>3×</b>.</li>
              <li><b>3 or more</b> BONUS ducks award <b>15 BETO RESPINS</b> (free plays). Tap <b>AUTO</b> to spin hands-free with stop rules.</li>
            </ul>
            <button className="ld-btn spin ld-howto-start" onClick={dismissHowTo} data-testid="ld-howto-start">LET'S PLAY</button>
            <button className="ld-howto-rules" onClick={() => { dismissHowTo(); setPaytableTab('PAYS'); setPaytableOpen(true); }} data-testid="ld-howto-paytable">See full paytable →</button>
          </div>
        </div>
      )}

      <PaytableModal
        open={paytableOpen}
        initialTab={paytableTab}
        betCredits={wagerCredits}
        payoutFactor={payoutFactor}
        onClose={() => { play('ui_button'); setPaytableOpen(false); }}
      />
      <JackpotWinOverlay win={jackpotWin} onDone={() => setJackpotWin(null)} />
    </div>
  );
};

export default LuckyDucky;

